import React, { useMemo, useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
  FilterChangedEvent,
  GridReadyEvent,
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SortModelItem
} from 'ag-grid-enterprise'
import dayjs from 'dayjs'
import CustomAgGrid from '../../../../shared/components/custom/Grid/CustomAgGrid'
import { Voucher, VoucherQueryParams, VoucherQuerySort } from '../../../../pages/reporting/vouchers/types'
import { contextCompanyIdSelector } from '../../../../redux/context/company/selectors'
import { TaskApplication } from '../../../../pages/superuser/types'
import { useAuthority } from '../../../../utils/Authorizable/authorize'
import { useCtxVoucherTypes } from '../../queries/useCtxVouchersTypes'
import { useCtxCustomers } from '../../../customer'
import { getVoucherTableColumns } from './VoucherTableColumns'
import TransactionDetailRenderer from './TransactionDetailRenderer'
import { VoucherActionsContext } from './VoucherActionsCellRenderer'
import { fetchVouchers } from '../../service/voucherService'
import { useDimensionQuery } from '../../../dimension/hooks/hooks'
import { getRequestParams } from '../../utils/voucherUtils'

export interface VoucherTableProps {
  /** Alkuarvot haulle - grid-filtterit voivat ylikirjoittaa osan (from, until, voucherType, jne.) */
  initialParams?: Partial<VoucherQueryParams>
  /** Yrityksen ID - jos annettu, ohittaa Redux-kontekstin yrityksen */
  companyId?: string
  /** Dimension query string */
  dimensions?: string
  /** Taulukon korkeus */
  height?: number | string
  /** Näytä action-sarake (edit, copy, delete, attachments) */
  showActions?: boolean
  /** Näytä sarakekohtaiset filtterit (default: true) */
  showFilters?: boolean
  /** Callback kun tosite poistetaan */
  deleteVoucher?: (value: Voucher) => void
  /** Callback kun tositetta muokataan */
  editVoucher?: (value: Voucher) => void
  /** Callback kun tosite kopioidaan */
  copyVoucher?: (value: Voucher) => void
  /** Callback kun data on ladattu */
  onDataLoaded?: () => void
  /** Callback kun gridin filtterit muuttuvat - palauttaa yhdistetyt parametrit (initialParams + grid filtterit) */
  onExportParamsChange?: (params: Partial<VoucherQueryParams>) => void
  /** Muuta tätä pakottaaksesi uudelleenlatauksen */
  refreshKey?: number
  /** Callback kun liitetiedostoja halutaan avata */
  onOpenAttachments?: (voucher: Voucher) => void
}

const VoucherTable: React.FC<VoucherTableProps> = ({
  initialParams,
  companyId: companyIdProp,
  dimensions,
  height = 800,
  showActions = false,
  showFilters = true,
  deleteVoucher,
  editVoucher,
  copyVoucher,
  onDataLoaded,
  onExportParamsChange,
  refreshKey,
  onOpenAttachments
}) => {
  const contextCompanyId = useSelector(contextCompanyIdSelector)
  const companyId = companyIdProp ?? contextCompanyId
  const dimensionQuery = useDimensionQuery()
  const gridApiRef = useRef<GridApi<Voucher>>()

  // Use refs to avoid recreating datasource on prop changes
  const initialParamsRef = useRef(initialParams)
  initialParamsRef.current = initialParams
  const onDataLoadedRef = useRef(onDataLoaded)
  onDataLoadedRef.current = onDataLoaded
  const onExportParamsChangeRef = useRef(onExportParamsChange)
  onExportParamsChangeRef.current = onExportParamsChange

  // Use provided dimensions or fall back to context dimension query
  const effectiveDimensions = dimensions ?? dimensionQuery

  const voucherActionsEnabled = useAuthority({
    accountingSoftware: TaskApplication.FINADECK
  })
  const { data: voucherTypes = [] } = useCtxVoucherTypes()
  const { data: customers = [] } = useCtxCustomers()

  const effectiveShowActions = showActions && voucherActionsEnabled

  // Refresh voucher grid when initialParams or refreshKey change
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.refreshServerSide({ purge: true })
    }
  }, [initialParams, refreshKey])

  const columns = useMemo(() => {
    const cols = getVoucherTableColumns({
      voucherTypes,
      customers,
      showActions: effectiveShowActions
    })

    if (!showFilters) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      return cols.map(({ filter, filterParams, dateComponent, ...rest }) => rest)
    }

    return cols
  }, [voucherTypes, customers, effectiveShowActions, showFilters])

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: false,
      suppressHeaderMenuButton: true
    }),
    []
  )

  const context: VoucherActionsContext = useMemo(
    () => ({
      editVoucher,
      copyVoucher,
      deleteVoucher,
      openAttachments: onOpenAttachments
    }),
    [editVoucher, copyVoucher, deleteVoucher, onOpenAttachments]
  )

  const transformSortModel = useCallback((sortModel: SortModelItem[]): VoucherQuerySort[] | undefined => {
    if (sortModel.length === 0) return undefined

    return sortModel.map(item => {
      let field = item.colId
      if (field === 'invoicePartnerId') field = 'invoice.partner.name'
      else if (field === 'invoiceNumber') field = 'invoice.number'
      else if (field === 'invoice.paymentDate') field = 'invoice.paymentDate'

      return {
        field,
        direction: item.sort === 'asc' ? 'ASC' : 'DESC'
      }
    })
  }, [])

  const transformFilterModel = useCallback(
    (filterModel: Record<string, any>): Partial<VoucherQueryParams> => {
      const result: Partial<VoucherQueryParams> = {}

      if (filterModel.guid) {
        result.guid = filterModel.guid.filter
      }

      if (filterModel.type) {
        result.voucherType = filterModel.type.values
      }

      if (filterModel.invoicePartnerId) {
        result.invoicePartnerId = filterModel.invoicePartnerId.values
      }

      if (filterModel.invoiceNumber) {
        result.invoiceNumber = filterModel.invoiceNumber.filter
      }

      if (filterModel.description) {
        result.voucherDesc = filterModel.description.filter
      }

      // Handle date filter
      if (filterModel.date?.dateFrom) {
        result.from = dayjs(filterModel.date.dateFrom)
      } else if (showFilters) {
        result.from = undefined
      }
      if (filterModel.date?.dateTo) {
        result.until = dayjs(filterModel.date.dateTo)
      } else if (showFilters) {
        result.until = undefined
      }

      return result
    },
    [showFilters]
  )

  const serverSideDatasource: IServerSideDatasource = useMemo(
    () => ({
      getRows: async (params: IServerSideGetRowsParams) => {
        if (!companyId) {
          params.fail()
          return
        }

        const { startRow = 0, endRow = 100, sortModel, filterModel } = params.request
        const pageSize = endRow - startRow
        const page = Math.floor(startRow / pageSize) + 1

        const sort = transformSortModel(sortModel)
        const filters = transformFilterModel(filterModel || {})

        const requestParams: Partial<VoucherQueryParams> = {
          ...initialParamsRef.current,
          ...filters,
          sort,
          page,
          pageSize,
          paginate: true
        }

        try {
          const result = await fetchVouchers(getRequestParams(requestParams), companyId, effectiveDimensions)

          params.success({
            rowData: result.vouchers,
            rowCount: result.results
          })

          onDataLoadedRef.current?.()
        } catch (error) {
          console.error('Failed to fetch vouchers:', error)
          params.fail()
        }
      }
    }),
    [companyId, effectiveDimensions, transformSortModel, transformFilterModel]
  )

  const onGridReady = useCallback(
    (event: GridReadyEvent<Voucher>) => {
      gridApiRef.current = event.api

      // Set initial date filter from initialParams (only when filters are enabled)
      if (showFilters && (initialParams?.from || initialParams?.until)) {
        const dateFilter: any = { filterType: 'date', type: 'inRange' }
        if (initialParams.from) {
          dateFilter.dateFrom = dayjs(initialParams.from).format('YYYY-MM-DD')
        }
        if (initialParams.until) {
          dateFilter.dateTo = dayjs(initialParams.until).format('YYYY-MM-DD')
        }

        event.api.setColumnFilterModel('date', dateFilter).then(() => {
          event.api.onFilterChanged()
        })
      }
    },
    [initialParams, showFilters]
  )

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent<Voucher>) => {
      if (!onExportParamsChangeRef.current) return
      const filterModel = event.api.getFilterModel() || {}
      const filters = transformFilterModel(filterModel)
      const merged: Partial<VoucherQueryParams> = {
        ...initialParamsRef.current,
        ...filters
      }
      onExportParamsChangeRef.current(merged)
    },
    [transformFilterModel]
  )

  return (
    <div style={{ height }}>
      <CustomAgGrid
        columnDefs={columns}
        defaultColDef={defaultColDef}
        context={context}
        onGridReady={onGridReady}
        onFilterChanged={handleFilterChanged}
        getRowId={rowParams => String(rowParams.data.id)}
        rowModelType="serverSide"
        serverSideDatasource={serverSideDatasource}
        cacheBlockSize={100}
        masterDetail
        detailCellRenderer={TransactionDetailRenderer}
        getRowHeight={params => {
          if (params.node.detail) {
            const rowTransactions = (params.data as Voucher)?.transactions || []
            if (rowTransactions.length === 0) return 36
            const maxRows = 10
            const visibleRows = Math.min(rowTransactions.length, maxRows)
            return 43 + visibleRows * 24 + 16
          }
          return undefined
        }}
        pagination
        paginationPageSize={20}
        paginationPageSizeSelector={[20, 50, 100]}
        tooltipShowDelay={500}
        cellSelection
        statusBar={{
          statusPanels: [
            { statusPanel: 'agTotalRowCountComponent' },
            { statusPanel: 'agSelectedRowCountComponent' },
            { statusPanel: 'agAggregationComponent' }
          ]
        }}
      />
    </div>
  )
}

export default VoucherTable
